"use client";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "next/navigation";
import DOMPurify from "dompurify";
import SuccessMessage from "./SuccessMessage";
import { jobAPI } from "@/services/pricing";
import "./JobDetails.css";
import ApplicationForm from "./ApplicationForm ";
import CommonButton from "./CommonButton";

const JobDetails = ({ jobData }) => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  // const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [applicationError, setApplicationError] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    country: "",
    totalExperienceYears: "",
    skills: "",
    currentCompany: "",
    currentTitle: "",
    expectedCTC: "",
    currentCTC: "",
    earliestStartDate: "",
    resume: null,
    privacyConsent: false,
    customAnswers: {},
  });
// const fetchJobDetail = useCallback(async () => {
//   if (!id) return;
//   setLoading(true);
//   const { data, error } = await jobAPI.fetchJobById(id);
//   setJob(data);
//   setError(error);
//   setLoading(false);
// }, [id]);

// useEffect(() => {
//   fetchJobDetail();
// }, [fetchJobDetail]);

useEffect(() => {
  if (!jobData) return;
  setJob(jobData);
}, [jobData])

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      city: "",
      country: "",
      totalExperienceYears: "",
      skills: "",
      currentCompany: "",
      currentTitle: "",
      expectedCTC: "",
      currentCTC: "",
      earliestStartDate: "",
      resume: null,
      privacyConsent: false,
      customAnswers: {},
    });
    setResumeFile(null);
    setApplicationError(null);
  };

  const handleApplyClick = () => {
    setShowModal(true);
    setShowSuccess(false);
    setApplicationError(null);

    // Initialize custom answers
    const initialAnswers = {};
    if (job.customQuestions && job.customQuestions.length > 0) {
      job.customQuestions.forEach((question) => {
        initialAnswers[question._id] = question.type === "boolean" ? null : "";
      });
    }
    setFormData((prev) => ({
      ...prev,
      customAnswers: initialAnswers,
    }));
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    setShowSuccess(false);
    resetForm();
    document.body.style.overflow = "auto";
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (applicationError) {
      setApplicationError(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setResumeFile(file);
    setFormData((prev) => ({
      ...prev,
      resume: file,
    }));
  };

  const handleCustomQuestionChange = (id, value) => {
    setFormData((prev) => ({
      ...prev,
      customAnswers: {
        ...prev.customAnswers,
        [id]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setApplicationError(null);

    const result = await jobAPI.submitApplication(
      job._id,
      formData,
      resumeFile
    );

    setSubmitting(false);

    if (result.success) {
      setShowSuccess(true);
      resetForm();
    } else {
      if (
        result.message.toLowerCase().includes("already applied") ||
        result.message.toLowerCase().includes("duplicate")
      ) {
        setApplicationError(
          "You have already applied for this position. Please check your email for updates."
        );
      } else {
        setApplicationError(result.message);
      }
    }
  };

  if (!job) return <div className="job-details-loading">Loading...</div>;
  if (error) return <div className="job-details-error">{error}</div>;
  // if (!job) return <div className="job-details-empty">No job found</div>;

  const formatSalary = (min, max, currency) => {
    const formatter = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 0,
    });
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  };

  const formatExperience = (min, max) => {
    if (min === max) return `${min} year${min !== 1 ? "s" : ""}`;
    return `${min}-${max} years`;
  };

  return (
    <>
      <div className="job-details-wrapper">
        <div className="job-details-container">
          {/* Hero Section */}
          <div className="job-hero-section">
            <div className="hero-background">
              <div className="hero-circle hero-circle-1"></div>
              <div className="hero-circle hero-circle-2"></div>
            </div>

            <div className="hero-content">
              <div className="hero-badges">
                <span
                  className={`status-badge-modern ${
                    job.isOpen ? "badge-open" : "badge-closed"
                  }`}
                >
                  <span className="badge-dot"></span>
                  {job.isOpen ? "Actively Hiring" : "Position Closed"}
                </span>
                <span className="position-badge">
                  {job.totalPositions}{" "}
                  {job.totalPositions === 1 ? "Position" : "Positions"}
                </span>
              </div>

              <h1 className="job-title-hero">{job.title}</h1>
              <p className="job-team-hero">{job.team}</p>

              <div className="hero-info-grid">
                <div className="hero-info-item">
                  <svg
                    className="info-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span>{job.locationType}</span>
                </div>
                <div className="hero-info-item">
                  <svg
                    className="info-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>
                    {formatExperience(job.minExp, job.maxExp)} experience
                  </span>
                </div>
                <div className="hero-info-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="Layer_1"
                    className="info-icon"
                    data-name="Layer 1"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="512"
                    height="512"
                  >
                    <path d="M16.5,0c-4.206,0-7.5,1.977-7.5,4.5v2.587c-.484-.057-.985-.087-1.5-.087C3.294,7,0,8.977,0,11.5v8c0,2.523,3.294,4.5,7.5,4.5,3.416,0,6.231-1.304,7.167-3.146,.597,.087,1.207,.146,1.833,.146,4.206,0,7.5-1.977,7.5-4.5V4.5c0-2.523-3.294-4.5-7.5-4.5Zm0,2c3.148,0,5.5,1.32,5.5,2.5s-2.352,2.5-5.5,2.5-5.5-1.32-5.5-2.5,2.352-2.5,5.5-2.5ZM7.5,9c3.148,0,5.5,1.32,5.5,2.5s-2.352,2.5-5.5,2.5-5.5-1.32-5.5-2.5,2.352-2.5,5.5-2.5ZM2,14.582c1.36,.875,3.303,1.418,5.5,1.418s4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5s-5.5-1.32-5.5-2.5v-.918Zm5.5,7.418c-3.148,0-5.5-1.32-5.5-2.5v-.918c1.36,.875,3.303,1.418,5.5,1.418s4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Zm9-3c-.514,0-1.012-.047-1.5-.116v-1.98c.492,.058,.99,.096,1.5,.096,2.197,0,4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Zm0-4c-.514,0-1.012-.047-1.5-.116v-1.98c.492,.058,.99,.096,1.5,.096,2.197,0,4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Zm0-4c-.542,0-1.066-.051-1.578-.127-.198-.887-.809-1.684-1.721-2.321,.992,.285,2.106,.449,3.299,.449,2.197,0,4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Z" />
                  </svg>
                  <span>
                    {formatSalary(
                      job.salaryRange.min,
                      job.salaryRange.max,
                      job.salaryRange.currency
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="job-content-grid">
            {/* Left Column */}
            <div className="job-main-content">
              {/* Description */}
              <div className="job-card">
                <div className="card-header">
                  <div className="card-icon card-icon-blue">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  </div>
                  <h2>Job Description</h2>
                </div>
                <div
                  className="description-html"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(job.descriptionHTML),
                  }}
                />
              </div>

              {/* Keywords Section */}
              {/* {job.keywords?.length > 0 && (
                <div className="job-card">
                  <div className="card-header">
                    <div className="card-icon card-icon-orange">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                        <line x1="7" y1="7" x2="7.01" y2="7"></line>
                      </svg>
                    </div>
                    <h2>Keywords</h2>
                  </div>
                  <div className="keywords-container">
                    {job.keywords.map((keyword, i) => (
                      <span key={i} className="keyword-tag">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )} */}

              {/* Responsibilities */}
              {job.responsibilities?.length > 0 &&
                job.responsibilities.some((r) => r.trim() !== "") && (
                  <div className="job-card">
                    <div className="card-header">
                      <div className="card-icon card-icon-green">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                      <h2>Key Responsibilities</h2>
                    </div>
                    <ul className="responsibilities-list">
                      {job.responsibilities.map((item, i) => (
                        <li key={i}>
                          <span className="list-number">{i + 1}</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* Requirements */}
              {job.requirements?.length > 0 &&
                job.requirements.some((r) => r.trim() !== "") && (
                  <div className="job-card">
                    <div className="card-header">
                      <div className="card-icon card-icon-purple">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <circle cx="12" cy="8" r="7"></circle>
                          <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                        </svg>
                      </div>
                      <h2>Requirements</h2>
                    </div>
                    <ul className="requirements-list">
                      {job.requirements.map((item, i) => (
                        <li key={i}>
                          <svg
                            className="check-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                          </svg>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>

            {/* Right Sidebar */}
            <div className="job-sidebar">
              {/* Quick Overview */}
              <div className="job-card sidebar-sticky">
                <h3 className="sidebar-title">Quick Overview</h3>
                <div className="overview-list">
                  <div className="overview-item">
                    <div className="overview-icon overview-icon-blue">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <rect
                          x="2"
                          y="7"
                          width="20"
                          height="14"
                          rx="2"
                          ry="2"
                        ></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                    <div className="overview-details">
                      <p className="overview-label">Experience</p>
                      <p className="overview-value">
                        {formatExperience(job.minExp, job.maxExp)}
                      </p>
                    </div>
                  </div>

                  <div className="overview-item">
                    <div className="overview-icon overview-icon-green">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        id="Layer_1"
                        data-name="Layer 1"
                        viewBox="0 0 24 24"
                        width="512"
                        height="512"
                      >
                        <path d="M16.5,0c-4.206,0-7.5,1.977-7.5,4.5v2.587c-.484-.057-.985-.087-1.5-.087C3.294,7,0,8.977,0,11.5v8c0,2.523,3.294,4.5,7.5,4.5,3.416,0,6.231-1.304,7.167-3.146,.597,.087,1.207,.146,1.833,.146,4.206,0,7.5-1.977,7.5-4.5V4.5c0-2.523-3.294-4.5-7.5-4.5Zm0,2c3.148,0,5.5,1.32,5.5,2.5s-2.352,2.5-5.5,2.5-5.5-1.32-5.5-2.5,2.352-2.5,5.5-2.5ZM7.5,9c3.148,0,5.5,1.32,5.5,2.5s-2.352,2.5-5.5,2.5-5.5-1.32-5.5-2.5,2.352-2.5,5.5-2.5ZM2,14.582c1.36,.875,3.303,1.418,5.5,1.418s4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5s-5.5-1.32-5.5-2.5v-.918Zm5.5,7.418c-3.148,0-5.5-1.32-5.5-2.5v-.918c1.36,.875,3.303,1.418,5.5,1.418s4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Zm9-3c-.514,0-1.012-.047-1.5-.116v-1.98c.492,.058,.99,.096,1.5,.096,2.197,0,4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Zm0-4c-.514,0-1.012-.047-1.5-.116v-1.98c.492,.058,.99,.096,1.5,.096,2.197,0,4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Zm0-4c-.542,0-1.066-.051-1.578-.127-.198-.887-.809-1.684-1.721-2.321,.992,.285,2.106,.449,3.299,.449,2.197,0,4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Z" />
                      </svg>
                    </div>
                    <div className="overview-details">
                      <p className="overview-label">Salary Range</p>
                      <p className="overview-value">
                        {formatSalary(
                          job.salaryRange.min,
                          job.salaryRange.max,
                          job.salaryRange.currency
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="overview-item">
                    <div className="overview-icon overview-icon-orange">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    </div>
                    <div className="overview-details">
                      <p className="overview-label">Location</p>
                      <p className="overview-value">{job.locationType}</p>
                    </div>
                  </div>

                  <div className="overview-item">
                    <div className="overview-icon overview-icon-purple">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    </div>
                    <div className="overview-details">
                      <p className="overview-label">Open Positions</p>
                      <p className="overview-value">{job.totalPositions}</p>
                    </div>
                  </div>
                </div>

                {/* Apply Button */}
                <div className="apply-button-container">
                  <CommonButton
                    className={`w-100 border-radius-16 ${
                      !job.isOpen ? "disabled" : ""
                    }`}
                    disabled={!job.isOpen}
                    onClick={handleApplyClick}
                    label={job.isOpen ? "Apply Now →" : "Position Closed"}
                  />
                </div>
              </div>

              {/* Custom Questions */}
              {job?.customQuestions?.length > 0 && (
                <div className="job-card questions-card">
                  <h3 className="sidebar-title">Application Questions</h3>
                  <p className="questions-info">
                    {`You'll be asked to answer ${
                      job?.customQuestions.length
                    } additional question${
                      job?.customQuestions.length !== 1 ? "s" : ""
                    } during application.`}
                  </p>
                  <ul className="questions-list">
                    {job?.customQuestions.map((q) => (
                      <li key={q._id}>
                        <span className="question-bullet"></span>
                        <span className="question-text">
                          {q.label}{" "}
                          {q.required && (
                            <span className="required-mark">*</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="career-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Apply for {job?.title}</h2>
              <button className="close-button" onClick={closeModal}>
                ×
              </button>
            </div>

            {showSuccess ? (
              <SuccessMessage jobTitle={job?.title} onClose={closeModal} />
            ) : (
              <>
                {applicationError && (
                  <div className="application-error">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <p>{applicationError}</p>
                  </div>
                )}

                <ApplicationForm
                  formData={formData}
                  selectedJob={job}
                  resumeFile={resumeFile}
                  submitting={submitting}
                  onInputChange={handleInputChange}
                  onFileChange={handleFileChange}
                  onCustomQuestionChange={handleCustomQuestionChange}
                  onSubmit={handleSubmit}
                  onCancel={closeModal}
                />
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default JobDetails;
